/**
 * Actions tab. Mini PRD: "Accepted actions land in the Actions system and
 * behave like any other action from then on" - including completion, which
 * closes the OKR loop (capture-derived action completion, trackable
 * end-to-end).
 */
import { Box, Stack, Typography } from '@mui/material';

import { PageTemplate } from '@karehero/llama/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';
import { Card } from '@karehero/llama/components/atoms/Card/Card';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { Checkbox } from '@karehero/llama/components/atoms/Checkbox/Checkbox';

import { useNavigation } from '@/hooks/useNavigationContext';
import { useCaptureFlow } from './CaptureFlowContext';
import { palette } from './components/palette';

export const ActionsTab = () => {
  const navigate = useNavigation();
  const { savedCaptures, completedActionKeys, toggleActionComplete } =
    useCaptureFlow();

  const items = savedCaptures.flatMap((capture) =>
    capture.acceptedActions.map((action) => ({
      action,
      captureId: capture.id,
      sourceFile: capture.doc.fileName,
      key: `${capture.id}:${action.id}`,
    })),
  );

  const completedCount = items.filter((item) =>
    completedActionKeys.includes(item.key),
  ).length;

  return (
    <PageTemplate
      maxWidth='sm'
      headerSlot={
        <PageHeader
          title='Actions'
          description={
            items.length > 0
              ? `${completedCount} of ${items.length} done. Your next steps, all in one place.`
              : 'Your next steps, all in one place.'
          }
        />
      }
      contentSlot={
        items.length === 0 ? (
          <Card isFullWidth>
            <Stack spacing={1} sx={{ alignItems: 'center', py: 2 }}>
              <Icon icon='clipboard-list' fontSize={28} />
              <Typography>Nothing here yet</Typography>
              <Typography
                sx={{
                  color: palette.mutedText,
                  fontSize: 13,
                  textAlign: 'center',
                }}
              >
                Actions you accept from captured documents will appear here.
              </Typography>
            </Stack>
          </Card>
        ) : (
          <Stack spacing={1.5}>
            {items.map(({ action, captureId, sourceFile, key }) => {
              const isComplete = completedActionKeys.includes(key);
              return (
                <Card key={key} isFullWidth isPaddingSm>
                  <Stack
                    sx={{ alignItems: 'flex-start' }}
                    direction='row'
                    spacing={1}
                  >
                    <Box sx={{ pt: 0.25 }}>
                      <Checkbox
                        id={`complete-${key}`}
                        value={isComplete}
                        onChange={() =>
                          toggleActionComplete(captureId, action.id)
                        }
                      />
                    </Box>
                    <Stack
                      spacing={0.5}
                      sx={{ minWidth: 0, opacity: isComplete ? 0.6 : 1 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          textDecoration: isComplete ? 'line-through' : 'none',
                        }}
                      >
                        {action.title}
                      </Typography>
                      <Typography
                        sx={{ color: palette.mutedText, fontSize: 13 }}
                      >
                        {action.description}
                      </Typography>
                      <Box
                        component='button'
                        aria-label={`View source document ${sourceFile}`}
                        onClick={() => navigate(`/documents/${captureId}`)}
                        sx={{
                          alignSelf: 'flex-start',
                          backgroundColor: palette.lavender,
                          color: palette.lavenderInk,
                          border: 'none',
                          borderRadius: 99,
                          padding: '2px 8px',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        From {sourceFile}
                        {action.isTweaked ? ' · edited by you' : ''}
                      </Box>
                    </Stack>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        )
      }
    />
  );
};

export default ActionsTab;
